// src/stores/teacherStore.ts
// State management לאפליקציית גננת — Zustand

import { create } from 'zustand';

// ==========================================
// Types
// ==========================================

export interface BudgetSource {
  publicId: string;
  name: string;
  description?: string;
  sourceType: number;
  sourceTypeName: string;
  totalBudget: number;
  totalSpent: number;
  remainingBudget: number;
  periodStart: string;
  periodEnd: string;
  periodType?: number;
  maxSingleExpense?: number;
  sourceMetadata?: string;
  isActive: boolean;
  notes?: string;
}

export interface Expense {
  publicId: string;
  budgetSourcePublicId: string;
  budgetSourceName: string;
  categoryId?: number;
  categoryName?: string;
  categoryIcon?: string;
  description: string;
  amount: number;
  expenseDate: string;
  invoiceUrl?: string;
  invoiceOcrStatus: number;
  invoiceOcrConfidence?: number;
  status: number;
  statusName: string;
  notes?: string;
  createdAt: string;
}

export interface PurchaseList {
  publicId: string;
  name: string;
  status: number;
  statusName: string;
  budgetSourcePublicId?: string;
  budgetSourceName?: string;
  totalEstimated?: number;
  totalActual?: number;
  itemCount: number;
  purchasedItemCount: number;
  notes?: string;
  createdAt: string;
}

export interface PurchaseListItem {
  id: number;
  productId?: number;
  productPublicId?: string;
  productName: string;
  categoryId?: number;
  categoryName?: string;
  quantity: number;
  unit?: string;
  estimatedPrice?: number;
  actualPrice?: number;
  isChargeable: boolean;
  isPurchased: boolean;
  isReceived: boolean;
  receivedQuantity?: number;
  receivedAt?: string;
  receivedByName?: string;
  notes?: string;
  sortOrder: number;
  isAdHoc: boolean;
}

export interface Product {
  publicId: string;
  name: string;
  categoryId?: number;
  categoryName?: string;
  categoryIcon?: string;
  defaultUnit?: string;
  estimatedPrice?: number;
  isChargeable: boolean;
  ownerLevel: number;
  ownerLevelName: string;
  isActive: boolean;
}

export interface ExpenseCategory {
  id: number;
  name: string;
  icon?: string;
  ownerLevel: number;
  sortOrder: number;
}

// ==========================================
// Store
// ==========================================

interface TeacherState {
  // Budget
  budgetSources: BudgetSource[];
  setBudgetSources: (sources: BudgetSource[]) => void;

  // Expenses
  expenses: Expense[];
  setExpenses: (expenses: Expense[]) => void;

  // Purchase Lists
  purchaseLists: PurchaseList[];
  setPurchaseLists: (lists: PurchaseList[]) => void;

  // Product Catalog
  catalog: Product[];
  setCatalog: (products: Product[]) => void;

  // Categories
  categories: ExpenseCategory[];
  setCategories: (cats: ExpenseCategory[]) => void;

  // Loading states
  isLoadingBudgets: boolean;
  isLoadingExpenses: boolean;
  isLoadingPurchases: boolean;
  isLoadingCatalog: boolean;
  setLoading: (key: string, value: boolean) => void;

  // Reset
  resetStore: () => void;
}

export const useTeacherStore = create<TeacherState>()((set) => ({
  budgetSources: [],
  setBudgetSources: (sources) => set({ budgetSources: sources }),

  expenses: [],
  setExpenses: (expenses) => set({ expenses }),

  purchaseLists: [],
  setPurchaseLists: (lists) => set({ purchaseLists: lists }),

  catalog: [],
  setCatalog: (products) => set({ catalog: products }),

  categories: [],
  setCategories: (cats) => set({ categories: cats }),

  isLoadingBudgets: false,
  isLoadingExpenses: false,
  isLoadingPurchases: false,
  isLoadingCatalog: false,
  setLoading: (key, value) => set({ [`isLoading${key.charAt(0).toUpperCase() + key.slice(1)}`]: value } as any),

  resetStore: () => set({
    budgetSources: [],
    expenses: [],
    purchaseLists: [],
    catalog: [],
    categories: [],
    isLoadingBudgets: false,
    isLoadingExpenses: false,
    isLoadingPurchases: false,
    isLoadingCatalog: false,
  }),
}));

// ==========================================
// Helper: SourceType name mapping
// ==========================================
export const sourceTypeNames: Record<number, string> = {
  1: 'תקציב רשת',
  2: 'תקציב עירוני',
  3: 'ועד הורים',
  4: 'משרד החינוך',
  5: 'תרומה',
  6: 'אחר',
};

export const expenseStatusNames: Record<number, string> = {
  0: 'טיוטה',
  1: 'ממתין',
  2: 'מאושר',
  3: 'נדחה',
  4: 'דווח למקור',
};

export const purchaseListStatusNames: Record<number, string> = {
  0: 'טיוטה',
  1: 'נשלחה',
  2: 'נרכשה',
  3: 'חשבונית צורפה',
};
